import React, { FunctionComponent } from 'react';
import { Link, Headline, Paragraph, List, Span } from 'View/Common';
import { CustomScrollbar } from 'View/Component';
import { StyledModal } from '../Modal.style';

interface IProps {
  isOpen: boolean;
  toggle?: () => void;
}

export const AccessibilityModal: FunctionComponent<IProps> = ({ isOpen, toggle }) => {
  return (
    <StyledModal toggle={toggle} isOpen={isOpen} size="lg" headline={<Headline level={2}>Angaben zur Barrierefreiheit</Headline>}>
      <CustomScrollbar>
        <Paragraph>
          Diese Erklärung zur Barrierefreiheit gilt für die unter{' '}
          <Link href="https://www.7schlaefer.app" title="Zur Website www.7schlaefer.app">
            www.7schlaefer.app
          </Link>{' '}
          verfügbare App 7Schläfer. Getestet wurde die Software auf den Plattformen Android und iOS. Als Verantwortlicher für die App sind
          wir bemüht, unsere Software im Einklang mit den Bestimmungen des Barrierefreiheitsstärkungsgesetzes (BFSG) sowie der
          Barrierefreien-Informationstechnik-Verordnung (BITV 2.0) zur Umsetzung der Richtlinie (EU) 2016/2102 und der europäischen Norm für
          digitale Barrierefreiheit (EN 301 549) barrierefrei zugänglich zu machen.
        </Paragraph>
        <Headline level={3}>Wann wurde die Erklärung zur Barrierefreiheit erstellt bzw. aktualisiert?</Headline>
        <Paragraph>
          Diese Erklärung zur Barrierefreiheit (Stand: 15.01.2025) gilt für die verfügbare Software 7Schläfer und beruht auf einer im
          Zeitraum vom 1.12.2024 bis 10.01.2025 durchgeführten Selbstbewertung auf Basis des Entwurfs vom Stand 09.03.2021 des BIK
          BITV-Tests für mobile Apps{' '}
          <Link href="https://github.com/BIK-BITV/BIK-App-Test" title="Zur GitHub-Seite des BIK-BITV-App-Tests">
            https://github.com/BIK-BITV/BIK-App-Test
          </Link>{' '}
          sowie der Anforderungen des Barrierefreiheitsstärkungsgesetzes (BFSG).
        </Paragraph>
        <Headline level={3}>Welche Vorkehrungen sind getroffen, um die App barrierefrei zu gestalten?</Headline>
        <br />
        <Headline level={3}>Aufbau</Headline>
        <Paragraph>
          <List>
            {[
              <>Verwendung von Stylesheets für das Design (Bedienhilfe Sehen)</>,
              <>Bereitstellung aller Lern-Inhalte als Audio Files (Bedienhilfe Hören)</>,
              <>eindeutig identifizierbare Hyperlinks, in begleitenden Email-Aussendungen (Bedienhilfe Sehen)</>,
              <>Linkziele mit informativem „Title”-Attribut (Beispielsweise: Link öffnet PDF-Datei in neuem Fenster) (Bedienhilfe Sehen)</>,
              <>Links auf Nicht-HTML-Seiten mit Beschreibung zum verlinkten Datenformat (z.B. PDF, Word usw.) (Bedienhilfe Sehen)</>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Farben und Kontraste</Headline>
        <Paragraph>
          <List>
            {[
              <>farbige und grafische Informationen sind in der Regel auch ohne Farbe sinnvoll nutzbar (Bedienhilfe Sehen)</>,
              <>
                Nach Möglichkeit Vermeidung von problematischen Farbkombinationen (gleicher Grauwert in S/W-Umsetzung) (Bedienhilfe Sehen)
              </>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Fließtext und Überschriften</Headline>
        <Paragraph>
          <List>
            {[
              <>Einsatz variabler Schriftgrößen (Bedienhilfe Sehen)</>,
              <>Verwendung von Unterstreichungen nur bei Verlinkungen (Bedienhilfe Sehen)</>,
              <>Hintergrund und Schrift verfügen in der Regel über ausreichend Kontrast (Bedienhilfe Sehen)</>,
              <>Sicherung der Erkennbarkeit und Lesbarkeit von Textinhalten bei 200%-Vergrößerung (Bedienhilfe Sehen)</>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Textuelle Inhalte</Headline>
        <Paragraph>
          <List>
            {[
              <>Verwendung einer einfachen und klaren Sprache (Bedienhilfe Verstehen)</>,
              <>Vermeidung von Umgangssprache und Slangwörtern (Bedienhilfe Verstehen)</>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Grafische Inhalte</Headline>
        <Paragraph>
          <List>
            {[
              <>Verwendung von animierten Grafiken, wo möglich (Bedienhilfe Sehen)</>,
              <>Verzicht auf flackernde, blinkende oder sich bewegende Elemente (Bedienhilfe Sehen)</>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Multimediale Inhalte</Headline>
        <Paragraph>
          <List>
            {[
              <>
                Bereitstellung von Untertiteln bei Videos sowie Textfassungen bei Tondokumenten und Versendung verschriftlichter Texte auf
                Wunsch (E-Mail) (Bedienhilfe Hören/Sehen)
              </>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Officedokumente und PDFs</Headline>
        <Paragraph>
          <List>
            {[
              <>Hinterlegen des Dokumententitels in den Meta-Daten des Dokuments (Bedienhilfe Sehen)</>,
              <>Wahrung einer logischen Lesereihenfolge (Bedienhilfe Verstehen)</>,
            ]}
          </List>
        </Paragraph>
        <Headline level={3}>Wie barrierefrei ist die Anwendung insgesamt?</Headline>
        <Paragraph>
          Die Anwendung ist in Teilen barrierefrei. In Teilen sind noch nicht alle Anforderungen der
          Barrierefreie-Informationstechnik-Verordnung (BITV) 2.0 erfüllt – diese werden aktuell nachgebessert, um die WCAG-Erfolgskriterien
          der Konformitätsstufen A-AA in den fehlenden Bereichen abzudecken und sind auf der Roadmap für 2025.
        </Paragraph>
        <Headline level={3}>Barrieren melden: Kontakt zu den Ansprechpartner:innen</Headline>
        <Paragraph>
          Sie möchten uns bestehende Barrieren mitteilen oder Informationen zur Umsetzung der Barrierefreiheit erfragen? Bitte kontaktieren
          Sie uns per E-Mail unter{' '}
          <Link href="mailto:info@7schlaefer.app" title="E-Mail senden an: info@7schlaefer.app">
            info@7schlaefer.app
          </Link>
          . Ihre Anfragen werden durch unseren Support umgehend bearbeitet und beantwortet.
        </Paragraph>
        <Headline level={3}>Kontakt zur Schlichtungsstelle</Headline>
        <Paragraph>
          Wenn auch nach Ihrem Feedback an den oben genannten Kontakt keine zufriedenstellende Lösung gefunden wurde, können Sie sich an die
          Schlichtungsstelle nach § 16 BGG wenden. Die Schlichtungsstelle BGG hat die Aufgabe, bei Konflikten zum Thema Barrierefreiheit
          zwischen Menschen mit Behinderungen und öffentlichen Stellen des Bundes eine außergerichtliche Streitbeilegung zu unterstützen.
          Das Schlichtungsverfahren ist kostenlos. Es muss kein Rechtsbeistand eingeschaltet werden. Weitere Informationen zum
          Schlichtungsverfahren und den Möglichkeiten der Antragstellung erhalten Sie unter:{' '}
          <Link href="https://www.schlichtungsstelle-bgg.de" title="Link zur www.schlichtungsstelle-bgg.de">
            www.schlichtungsstelle-bgg.de
          </Link>
          . Direkt kontaktieren können Sie die Schlichtungsstelle BGG unter{' '}
          <Link href="mailto:info@schlichtungsstelle-bgg.de" title="E-Mail senden an: info@schlichtungsstelle-bgg.de">
            info@schlichtungsstelle-bgg.de
          </Link>
        </Paragraph>
      </CustomScrollbar>
    </StyledModal>
  );
};
